import { t } from '@lingui/core/macro';
import {
  NavioOverlayActions,
  NoddiButton,
  NoddiDialog,
  NoddiInfoBanner,
  noddiToast,
  useAppForm,
  zodEmailSchema,
  zodPhoneNumberSchema
} from 'noddi-ui-web';
import { getFirstErrorCode } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { z } from 'zod';
import { ContactDetailField, normalizeContactValue, useContactVerification } from '../hooks/useContactVerification';
import { getCommonTranslations } from '../utils/commonTranslations';

const FORM_ID = 'contact-update-form';

const getValidationSchema = (field: ContactDetailField, currentValue: string, requiresVerification: boolean) =>
  z
    .object({
      value: field === 'email' ? zodEmailSchema : zodPhoneNumberSchema,
      verificationCode: z.string()
    })
    .superRefine((value, context) => {
      if (
        requiresVerification &&
        normalizeContactValue(field, value.value) !== currentValue &&
        !value.verificationCode.trim()
      ) {
        context.addIssue({
          code: 'custom',
          message: field === 'email' ? t`Enter the email confirmation code` : t`Enter the SMS confirmation code`,
          path: ['verificationCode']
        });
      }
    });

type FormValues = {
  value: string;
  verificationCode: string;
};

type Props = {
  field: ContactDetailField;
  onClose: () => void;
  onSuccess: () => void;
};

export const ContactDetailsForm = ({ field, onClose, onSuccess }: Props) => {
  const isEmail = field === 'email';

  return (
    <NoddiDialog open onClose={onClose} title={isEmail ? t`Edit email` : t`Edit phone number`}>
      <ContactUpdateFields
        field={field}
        onCancel={onClose}
        onSaved={() => {
          onClose();
          onSuccess();
        }}
      />
    </NoddiDialog>
  );
};

/**
 * Inner contact-update form without dialog chrome, so surfaces like the
 * data-quality popup can embed it inline and the account page keeps the
 * dialog wrapper.
 */
export const ContactUpdateFields = ({
  field,
  onCancel,
  onSaved,
  saveLabel
}: {
  field: ContactDetailField;
  /** Omit when the embedding surface has its own dismissal (e.g. the data-quality dialog footer). */
  onCancel?: () => void;
  onSaved: () => void;
  saveLabel?: string;
}) => {
  const { userData } = useAuthContext();
  const texts = getCommonTranslations().contactVerification;
  const currentValue = normalizeContactValue(field, userData?.user[field] ?? '');
  const isEmail = field === 'email';

  const {
    isSendingEmailVerification,
    isSendingPhoneNumberVerification,
    resetSendEmailVerification,
    resetUpdateContact,
    sendEmailVerificationError,
    sendVerification,
    submitContactUpdate,
    updateContactError,
    requiresVerification
  } = useContactVerification();

  const form = useAppForm({
    defaultValues: {
      value: currentValue,
      verificationCode: ''
    } satisfies FormValues,
    validators: {
      onSubmit: getValidationSchema(field, currentValue, requiresVerification)
    },
    onSubmit: async ({ value }) => {
      const contactValue = normalizeContactValue(field, value.value);

      // Nothing to save, but the surface still has to move on — inside the
      // data-quality sheet a silent no-op reads as a dead Save button, and its
      // recheck is what decides whether the stored value is actually acceptable.
      if (contactValue === currentValue) {
        onSaved();
        return;
      }

      // A rejected update (e.g. the value is already used by another account) is
      // shown inline below; the dialog stays open so the user can correct it.
      try {
        await submitContactUpdate(field, contactValue, value.verificationCode);
      } catch {
        return;
      }
      onSaved();
    }
  });

  const handleEmailVerification = async () => {
    const errors = await form.validateField('value', 'submit');
    if (errors.length === 0) {
      sendVerification('email', form.state.values.value, () => noddiToast.success(texts.codeSent));
    }
  };

  const handlePhoneNumberVerification = async () => {
    const errors = await form.validateField('value', 'submit');
    if (errors.length === 0) {
      sendVerification('phoneNumber', form.state.values.value, () => noddiToast.success(texts.codeSent));
    }
  };

  if (!userData) {
    return null;
  }

  const description = requiresVerification
    ? isEmail
      ? t`We'll send a confirmation code to your new email address. Enter it here before saving.`
      : t`We'll send a confirmation code by SMS to your new phone number. Enter it here before saving.`
    : t`Update your contact details.`;
  const fieldLabel = isEmail ? t`Email` : t`Phone number`;
  const verificationLabel = isEmail ? texts.emailVerificationCode : texts.phoneNumberVerificationCode;

  return (
    <div className='flex flex-col gap-4'>
      <p className='text-sm text-muted-foreground'>{description}</p>
      <form.AppForm>
        <form
          id={FORM_ID}
          className='flex flex-col gap-4'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.AppField
            name='value'
            listeners={{
              onChange: () => {
                form.setFieldValue('verificationCode', '');
                resetSendEmailVerification();
                resetUpdateContact();
              }
            }}
          >
            {(input) =>
              isEmail ? <input.Input label={fieldLabel} type='email' /> : <input.PhoneInput label={fieldLabel} />
            }
          </form.AppField>
          {updateContactError && getFirstErrorCode(updateContactError) === 'user_phone_or_email_already_exists' && (
            <NoddiInfoBanner
              variant='warning'
              title={t`Already in use`}
              texts={[
                {
                  value: t`This email address or phone number belongs to another account. Use a different one.`
                }
              ]}
            />
          )}
          {isEmail &&
            sendEmailVerificationError &&
            getFirstErrorCode(sendEmailVerificationError) === 'resource_already_exist' && (
              <NoddiInfoBanner
                variant='warning'
                title={t`Email already in use`}
                texts={[{ value: t`This email address belongs to another account. Use a different email address.` }]}
              />
            )}
          <form.Subscribe selector={(state) => state.values.value}>
            {(value) =>
              normalizeContactValue(field, value) !== currentValue && (
                <>
                  {requiresVerification && (
                    <NoddiButton
                      type='button'
                      variant='outline'
                      loading={isEmail ? isSendingEmailVerification : isSendingPhoneNumberVerification}
                      disabled={isEmail ? isSendingEmailVerification : isSendingPhoneNumberVerification}
                      onClick={() => void (isEmail ? handleEmailVerification() : handlePhoneNumberVerification())}
                    >
                      {isEmail ? texts.sendEmailCode : texts.sendSmsCode}
                    </NoddiButton>
                  )}
                  {requiresVerification && (
                    <form.AppField name='verificationCode'>
                      {(input) => <input.Input label={verificationLabel} />}
                    </form.AppField>
                  )}
                </>
              )
            }
          </form.Subscribe>
          <NavioOverlayActions>
            {onCancel && (
              <NoddiButton type='button' variant='outline' onClick={onCancel}>
                {t`Cancel`}
              </NoddiButton>
            )}
            <form.SubscribeButton formId={FORM_ID} label={saveLabel ?? t`Save`} />
          </NavioOverlayActions>
        </form>
      </form.AppForm>
    </div>
  );
};
